import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Search, Heart, Scale, ShoppingBag, User, LogOut, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import { api, resolveImageUrl } from "@/lib/api";

const NAV = [
  { slug: "iphone", label: "iPhone" },
  { slug: "mac", label: "Mac" },
  { slug: "ipad", label: "iPad" },
  { slug: "watch", label: "Watch" },
  { slug: "airpods", label: "AirPods" },
  { slug: "dyson", label: "Dyson" },
  { slug: "accessories", label: "Аксессуары" },
];

export default function Header() {
  const { user, isAuthed, isAdmin, logout } = useAuth();
  const { cart, wishlist, compare } = useShop();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchPanelRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchToggleRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!openSearch) return;
    const query = q.trim();
    if (!query) {
      setResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const { data } = await api.get("/catalog/products", { params: { q: query, limit: 6 }, signal: controller.signal });
        setResults(data);
      } catch (err) {
        if (err?.code !== "ERR_CANCELED") setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, openSearch]);

  useEffect(() => {
    if (!openSearch) return;
    searchInputRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenSearch(false);
    };
    const onClickOutside = (e) => {
      if (searchToggleRef.current?.contains(e.target)) return;
      if (searchPanelRef.current && !searchPanelRef.current.contains(e.target)) {
        setOpenSearch(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [openSearch]);

  const submitSearch = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    setOpenSearch(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const NavItem = ({ slug, label }) => (
    <NavLink
      to={`/catalog/${slug}`}
      data-testid={`nav-${slug}`}
      className={({ isActive }) =>
        `text-sm font-medium tracking-tight transition-colors hover:text-ink ${
          isActive ? "text-ink" : "text-muted"
        }`
      }
    >
      {label}
    </NavLink>
  );

  return (
    <header
      className="sticky top-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-2xl backdrop-saturate-150"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 lg:px-8">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-ink text-white">
              <span className="font-extrabold tracking-tightest text-sm">V</span>
            </div>
            <div className="leading-none">
              <div className="font-extrabold tracking-tightest text-ink text-lg">vip77</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted-soft">store</div>
            </div>
          </Link>
          <nav className="hidden items-center gap-6 lg:flex">
            {NAV.map((n) => (
              <NavItem key={n.slug} {...n} />
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            ref={searchToggleRef}
            data-testid="search-toggle"
            onClick={() => { setOpenMenu(false); setOpenSearch((v) => !v); }}
            className="grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-black/5"
            aria-label="Поиск"
          >
            <Search size={18} />
          </button>

          <Link
            to="/compare"
            data-testid="header-compare"
            className="relative hidden h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-black/5 md:grid"
            aria-label="Сравнение"
          >
            <Scale size={18} />
            {compare.count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ink px-1 text-[10px] font-bold text-white">
                {compare.count}
              </span>
            )}
          </Link>

          <Link
            to="/wishlist"
            data-testid="header-wishlist"
            className="relative hidden h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-black/5 md:grid"
            aria-label="Избранное"
          >
            <Heart size={18} />
            {wishlist.count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ink px-1 text-[10px] font-bold text-white">
                {wishlist.count}
              </span>
            )}
          </Link>

          <Link
            to="/cart"
            data-testid="header-cart"
            className="relative grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-black/5"
            aria-label="Корзина"
          >
            <ShoppingBag size={18} />
            {cart.count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
                {cart.count}
              </span>
            )}
          </Link>

          {isAuthed ? (
            <div className="hidden items-center gap-1 md:flex">
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="header-admin"
                  className="ml-2 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
                >
                  Админ
                </Link>
              )}
              <Link
                to="/account"
                data-testid="header-account"
                className="ml-1 flex items-center gap-2 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ink-soft"
              >
                <User size={14} />
                <span className="max-w-[100px] truncate">{user?.name || user?.email}</span>
              </Link>
              <button
                data-testid="header-logout"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="ml-1 grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-black/5 hover:text-ink"
                aria-label="Выйти"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login"
              className="hidden rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-ink-soft md:inline-block"
            >
              Войти
            </Link>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => { setOpenSearch(false); setOpenMenu((v) => !v); }}
            className="grid h-10 w-10 place-items-center rounded-full text-ink transition-colors hover:bg-black/5 lg:hidden"
            aria-label="Меню"
          >
            {openMenu ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Search dropdown */}
      {openSearch && (
        <div
          ref={searchPanelRef}
          className="border-t border-black/5 bg-white/95 backdrop-blur-xl"
          data-testid="search-panel"
        >
          <div className="mx-auto max-w-7xl px-4 py-4 lg:px-8">
            <form onSubmit={submitSearch} className="relative">
              <input
                ref={searchInputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск: iPhone, MacBook, AirPods…"
                inputMode="search"
                enterKeyHint="search"
                className="w-full rounded-full border border-line bg-white py-3 pl-5 pr-11 text-base outline-none transition-colors focus:border-ink sm:text-sm"
              />
              {q && (
                <button
                  type="button"
                  data-testid="search-clear"
                  onClick={() => { setQ(""); searchInputRef.current?.focus(); }}
                  className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-muted transition-colors hover:bg-black/5 hover:text-ink"
                  aria-label="Очистить"
                >
                  <X size={15} />
                </button>
              )}
            </form>

            {searchLoading && (
              <div className="mt-4 flex items-center justify-center py-6 text-sm text-muted">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-ink" />
              </div>
            )}

            {!searchLoading && q.trim() && results.length === 0 && (
              <div className="mt-4 py-6 text-center text-sm text-muted" data-testid="search-empty">
                Ничего не найдено по запросу «{q.trim()}»
              </div>
            )}

            {!searchLoading && results.length > 0 && (
              <>
                <div className="mt-3 grid max-h-[60vh] gap-1 overflow-y-auto">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`search-result-${p.slug}`}
                      onClick={() => {
                        setOpenSearch(false);
                        navigate(`/product/${p.slug}`);
                      }}
                      className="flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-black/5"
                    >
                      <img src={resolveImageUrl(p.images?.[0])} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-ink">{p.name}</div>
                        <div className="truncate text-xs text-muted">{p.subtitle}</div>
                      </div>
                      <div className="shrink-0 text-sm font-semibold text-ink">от {new Intl.NumberFormat("ru-RU").format(p.min_price)} ₽</div>
                    </button>
                  ))}
                </div>
                <button
                  data-testid="search-view-all"
                  onClick={() => {
                    setOpenSearch(false);
                    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
                  }}
                  className="mt-2 w-full rounded-xl px-3 py-2.5 text-center text-sm font-semibold text-ink transition-colors hover:bg-black/5"
                >
                  Показать все результаты →
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {openMenu && (
        <div className="border-t border-black/5 bg-white lg:hidden" data-testid="mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4">
            {NAV.map((n) => (
              <Link
                key={n.slug}
                to={`/catalog/${n.slug}`}
                onClick={() => setOpenMenu(false)}
                data-testid={`mobile-nav-${n.slug}`}
                className="rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-black/5"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Link to="/wishlist" onClick={() => setOpenMenu(false)} className="rounded-lg border border-line py-2 text-center text-xs font-semibold">Избранное · {wishlist.count}</Link>
              <Link to="/compare" onClick={() => setOpenMenu(false)} className="rounded-lg border border-line py-2 text-center text-xs font-semibold">Сравнение · {compare.count}</Link>
              <Link to="/cart" onClick={() => setOpenMenu(false)} className="rounded-lg bg-ink py-2 text-center text-xs font-semibold text-white">Корзина · {cart.count}</Link>
            </div>
            {isAuthed ? (
              <>
                <Link to="/account" onClick={() => setOpenMenu(false)} className="mt-2 rounded-lg bg-black/5 px-3 py-2 text-sm font-semibold text-ink">Личный кабинет</Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setOpenMenu(false)} className="rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink">Админ-панель</Link>
                )}
                <button onClick={async () => { await logout(); setOpenMenu(false); }} className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-danger">Выйти</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpenMenu(false)} className="mt-2 rounded-lg bg-ink px-3 py-2 text-center text-sm font-semibold text-white">Войти / Регистрация</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
